import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Grid, Typography, Stepper, Step, StepLabel, IconButton } from '@mui/material';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import '../../Comprobantes/components/seleccionComprobante/seleccionComprobante.css';
import CloseIcon from '@mui/icons-material/Close';
import { ADDENDAS_REGISTRY, AddendaId } from 'pantallas/Comprobantes/addendas/registry';
import { axiosClient, getToken } from 'Config/axiosConfig';
import { toast } from 'react-toastify';

type Props = { open: boolean; onClose: () => void; comprobante: any };

export default function AddendaWizard({ open, onClose, comprobante }: Props) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState<AddendaId | null>('sanofi');
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const item = useMemo(() => ADDENDAS_REGISTRY.find((i) => i.id === selected) || null, [selected]);
  const pre = item?.preconditions?.(comprobante) || {};

  useEffect(() => {
    if (!open) return;
    setStep(1);
    // si solo hay 1 addenda, auto-seleccionar y pasar a step 2
    if (ADDENDAS_REGISTRY.length === 1) {
      setSelected(ADDENDAS_REGISTRY[0].id);
      setStep(2);
    }
  }, [open]);

  useEffect(() => {
    // Prefill al entrar a step 2
    if (step !== 2 || !item || !comprobante?.id) return;
    (async () => {
      try {
        await getToken();
        const res = await axiosClient.get(`/comprobantes/${comprobante.id}/addendas/${item.apiType}`);
        if (res?.data?.data) setForm({ ...res.data.data });
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selected, comprobante?.id]);

  const guardar = async () => {
    if (!item) return;
    const check = item.validate(form);
    if (!check.valid) return toast.info(check.message || 'Datos incompletos');
    try {
      setLoading(true);
      const res = await axiosClient.post(`/comprobantes/${comprobante.id}/addendas`, {
        tipo: item.apiType,
        version: '1.0',
        data: form,
      });
      if (pre?.warning) toast.warn(pre.warning);
      if (res.data?.warning) toast.warn('Advertencia reportada por el servidor');
      toast.success('Addenda guardada');
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al guardar addenda');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const allowEnv = (process.env.REACT_APP_ADDENDA_SANOFI_ALLOWLIST || '')
    .split(',')
    .map((s) => Number(String(s).trim()))
    .filter((n) => !isNaN(n));
  const canUseSanofiWizard = allowEnv.length > 0 && allowEnv.includes(Number(comprobante?.empresa_id));

  return (
    <Box sx={{
      background: '#fff',
      p: 3,
      borderRadius: 3,
      minWidth: 860,
      maxWidth: 1100,
      width: '90vw',
      boxShadow: '0 16px 40px rgba(0,0,0,0.18)'
    }}>
      <Typography variant='h6' gutterBottom>Agregar Addenda</Typography>
      <Box sx={{
        mb: 3,
        background: '#00ACC8',
        borderRadius: 3,
        px: 2,
        py: 1.25,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
      }}>
        <IconButton onClick={onClose} size='small' sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '50%',
          boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
          zIndex: 2
        }}>
          <CloseIcon fontSize='small' />
        </IconButton>
        <Stepper activeStep={step - 1} alternativeLabel sx={{
          background: 'transparent !important',
          bgcolor: 'transparent !important',
          backgroundImage: 'none !important',
          boxShadow: 'none !important',
          minHeight: 0,
          p: 0,
          width: '100%',
          flex: 1,
          '& *': { background: 'transparent !important', backgroundImage: 'none !important' },
          '& .MuiStepLabel-label': {
            color: '#fff !important',
            fontWeight: 600,
            fontSize: 12,
            textTransform: 'uppercase',
          },
          '& .MuiStepLabel-root': { color: '#fff !important' },
          '& .MuiStepLabel-iconContainer': { color: '#fff !important' },
          '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.6) !important', fontSize: 22 },
          '& .MuiStepIcon-root.Mui-active': { color: '#fff !important' },
          '& .MuiStepIcon-root.Mui-completed': { color: '#fff !important' },
          '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.8) !important' },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#fff !important' },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#fff !important' },
          '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6) !important' },
          // Ocupa el 100% y separa uniformemente
          '& .MuiStepper-horizontal': { width: '100%' },
          '& .MuiStep-root': { flex: 1, minWidth: 0, paddingLeft: 0, paddingRight: 0 },
          '& .MuiStep-horizontal': { paddingLeft: 0, paddingRight: 0 },
          '& .MuiStepConnector-root': { left: 'calc(-50% + 12px)', right: 'calc(50% + 12px)' },
        }}>
          {[ 'PASO 1: SELECCIÓN DE ADDENDA', 'PASO 2: DATOS' ].map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      {step === 1 && (
        <Grid container justifyContent='center' spacing={1} sx={{ mt: 1, mb: 2, width: '80%', mx: 'auto', placeItems: 'center' }}>
          {(canUseSanofiWizard ? ADDENDAS_REGISTRY : []).map((r) => {
            const Icon = r.icon;
            return (
              <Grid item key={r.id}>
                <MDButton
                  onClick={() => {
                    setSelected(r.id);
                    setStep(2);
                  }}
                  style={{ backgroundColor: 'transparent' }}
                >
                  <Card sx={{ display: 'flex', width: 190, height: 200, alignItems: 'center', justifyContent: 'center', textAlign: 'center', m: 'auto', borderRadius: 2 }} className='customButonComprobante'>
                    <Box>
                      {Icon ? <Icon style={{ color: '#00ACC8', height: 70, width: 70 }} /> : null}
                    </Box>
                    <MDTypography variant='h5' fontWeight='medium' color='primary' align='center'>
                      {r.label}
                    </MDTypography>
                  </Card>
                </MDButton>
              </Grid>
            );
          })}
        </Grid>
      )}

      {step === 2 && item && (
        <Grid container spacing={2}>
          {pre?.warning && (
            <Grid item xs={12}>
              <Typography color={'#b26a00'}>{pre.warning}</Typography>
            </Grid>
          )}
          {item.fields.map((f) => (
            <Grid item xs={12} md={6} key={f.name}>
              <input style={{ display: 'none' }} />
              <Box>
                <Typography variant='caption'>{f.label}</Typography>
                <input
                  type={f.type === 'email' ? 'email' : 'text'}
                  value={form[f.name] || ''}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #e0e0e0' }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <MDButton variant='outlined' color='primary' onClick={onClose} disabled={loading}>Cancelar</MDButton>
        {step === 1 && (
          <MDButton variant='gradient' color='primary' onClick={() => setStep(2)} disabled={!selected}>Siguiente</MDButton>
        )}
        {step === 2 && (
          <MDButton variant='gradient' color='primary' onClick={guardar} disabled={loading}>Guardar</MDButton>
        )}
      </Box>
    </Box>
  );
}
